import React, { memo, useContext, useImperativeHandle, useMemo, useState } from 'react';
import getSymbolFromCurrency from 'currency-symbol-map';
import { runOnJS, useAnimatedProps, useDerivedValue } from 'react-native-reanimated';
import { View, Platform } from 'react-native';
import Text from 'design-system/Text';
import { AnimatedWave, CardView, rem, signPlusSymbolWidth, Spacer, symbolWidth, ToolTip } from 'design-system';
import BlurAmountView from 'design-system/BlurAmountView';
import { CurrencyAmountText } from 'design-system/CurrencyAmountText';
import { AnimatedTextInput } from 'design-system/AnimatedComponents';
import useStyles from 'hooks/useStyles';
import { useAppSelector } from 'store/hooks';
import { formatAmount } from 'utils/formatting';
import createStyleSheets from 'utils/createStyleSheets';
import { selectIsScrambled } from 'hooks/useScrambleMode';
import { InvestWalkthroughContext } from 'features/feed/components/walkthrough/context';
import * as config from '../constants';
import PriceChange from './PriceChange';
import ClaimFreeShare from './ClaimFreeShare';
import PortfolioChart from './PortfolioChart';
import { usePortfolioChartData } from '../hooks';
import formatAmountWorklet from '../worklets/formatAmount';
import { InvestPortfolioContext } from '../context/portfolio';
import { isIOS, isAndroid, isWeb } from '../../../constants';
import { selectIsTradingAccountAlpaca } from '../reducers/selectors';
import PriceChangeTimeSeccl from './PriceChangeTimeSeccl';
const zero = { value: 0, currency: 'GBP' };
export const PortfolioHeader = React.forwardRef(({ isInTab, onPortfolioChartLayout, hasInvestments }, ref) => {
    const isScrambled = useAppSelector(selectIsScrambled);
    const { positions, tradingAccount } = useContext(InvestPortfolioContext);
    const isTradingAccountAlpaca = useAppSelector(selectIsTradingAccountAlpaca);
    const isInInvestWalkthrough = useContext(InvestWalkthroughContext);
    const [profitLoss, profitLossPercent, period, loading, contextValue, refreshChart, changeMode, updateCharts] = usePortfolioChartData();
    useImperativeHandle(ref, () => ({ updateCharts }), [updateCharts]);
    const styles = useStyles(styleSet);
    const scrambled = useAppSelector((store) => store.utils.scrambled);
    const balance = useMemo(() => {
        if (isTradingAccountAlpaca) {
            if (config.CALCULATE_PORTFOLIO_VALUE_BASED_ON_POSITIONS) {
                let calculatedPortfolioValue = zero;
                if (positions) {
                    calculatedPortfolioValue = positions.reduce((previousValue, position) => ({
                        value: previousValue.value + position.amount.value,
                        currency: position.amount.currency,
                    }), zero);
                }
                return calculatedPortfolioValue;
            }
            return tradingAccount?.longPositionsMarketValue ?? zero;
        }
        return tradingAccount?.portfolioValue ?? zero;
    }, [positions, tradingAccount?.longPositionsMarketValue, tradingAccount?.portfolioValue, isTradingAccountAlpaca]);
    const balanceSymbol = useMemo(() => getSymbolFromCurrency(balance.currency) || '£', [balance.currency]);
    const fallbackInfo = React.useMemo(() => {
        const amount = formatAmount(Math.abs(balance.value), undefined, 2);
        const sign = balance.value < 0 ? '-' : '';
        const [decimal, fraction] = amount.split('.');
        return {
            decimal,
            fraction: `.${fraction}`,
            currencyAndSign: sign + balanceSymbol,
            currencyAndSignWidth: sign ? signPlusSymbolWidth : symbolWidth,
        };
    }, [balance.value, balanceSymbol]);
    const { originalDataShared, originalX } = contextValue || {};
    const infoSharedValue = useDerivedValue(() => {
        if (originalX?.value && originalDataShared?.value) {
            const x = parseInt(originalX.value, 10);
            const data = originalDataShared.value.equity;
            const valueToUse = data[x];
            if (typeof valueToUse === 'number') {
                const amount = formatAmountWorklet(Math.abs(valueToUse), '');
                const sign = valueToUse < 0 ? '-' : '';
                const [decimal, fraction] = amount.split('.');
                return {
                    decimal,
                    fraction: `.${fraction}`,
                    currencyAndSign: sign + balanceSymbol,
                    currencyAndSignWidth: sign ? signPlusSymbolWidth : symbolWidth,
                };
            }
        }
        return fallbackInfo;
    }, [balanceSymbol, fallbackInfo, originalDataShared, originalX]);
    // On web the three text inputs below don't size to their text, so the value
    // is mirrored into state and drawn as text instead
    const [webSelectedEquity, setWebSelectedEquity] = useState();
    useDerivedValue(() => {
        if (isWeb) {
            let selectedEquity;
            if (originalX?.value && originalDataShared?.value) {
                const valueToUse = originalDataShared.value.equity[parseInt(originalX.value, 10)];
                if (typeof valueToUse === 'number') {
                    selectedEquity = valueToUse;
                }
            }
            runOnJS(setWebSelectedEquity)(selectedEquity);
        }
    }, [originalDataShared, originalX]);
    const decimalProps = useAnimatedProps(() => ({
        text: infoSharedValue.value.decimal,
    }), [infoSharedValue]);
    const currencyProps = useAnimatedProps(() => ({
        text: infoSharedValue.value.currencyAndSign,
        ...(isAndroid
            ? {
                width: infoSharedValue.value.currencyAndSignWidth,
            }
            : {}),
    }), [infoSharedValue]);
    const fractionProps = useAnimatedProps(() => ({
        text: infoSharedValue.value.fraction,
    }), [infoSharedValue]);
    const balanceContainer = useMemo(() => (<View style={styles.balanceContainer}>
          <View style={styles.row}>
            {isWeb ? (<CurrencyAmountText size="L" value={{ amount: webSelectedEquity ?? balance.value, currency: balance.currency }}/>) : (<BlurAmountView enabled={isScrambled}>
                <View style={styles.amountView}>
                  <AnimatedTextInput editable={false} numberOfLines={1} allowFontScaling={false} animatedProps={currencyProps} defaultValue={fallbackInfo.currencyAndSign} style={[styles.currencyStyles, isAndroid && { width: fallbackInfo.currencyAndSignWidth }]}/>
                  <Spacer w={2}/>
                  <AnimatedTextInput editable={false} numberOfLines={1} allowFontScaling={false} animatedProps={decimalProps} style={styles.absAmountStyles} defaultValue={fallbackInfo.decimal}/>
                  <AnimatedTextInput editable={false} numberOfLines={1} allowFontScaling={false} animatedProps={fractionProps} style={styles.fractionStyles} defaultValue={fallbackInfo.fraction}/>
                </View>
              </BlurAmountView>)}
          </View>

          {!hasInvestments ? (<Text TextThin-14 Secondary>
              No portfolio performance to display
            </Text>) : (<View style={styles.moveUp}>
              {isTradingAccountAlpaca ? (<PriceChange withAmount period={period} scrambled={scrambled} percent={profitLossPercent} amount={{
                    value: profitLoss,
                    currency: tradingAccount?.currency ?? 'GBP',
                }} contextValue={contextValue} withTime={period === '1D' || period === '1W'}/>) : (<PriceChangeTimeSeccl scrambled={scrambled} period={period} percent={profitLossPercent} amount={{
                    value: profitLoss,
                    currency: tradingAccount?.currency ?? 'GBP',
                }} contextValue={contextValue}/>)}
            </View>)}
        </View>), [
        styles.balanceContainer,
        styles.row,
        styles.amountView,
        styles.currencyStyles,
        styles.absAmountStyles,
        styles.fractionStyles,
        styles.moveUp,
        isScrambled,
        currencyProps,
        fallbackInfo.currencyAndSign,
        fallbackInfo.currencyAndSignWidth,
        fallbackInfo.decimal,
        fallbackInfo.fraction,
        decimalProps,
        fractionProps,
        isTradingAccountAlpaca,
        scrambled,
        profitLossPercent,
        profitLoss,
        tradingAccount?.currency,
        contextValue,
        period,
        hasInvestments,
        webSelectedEquity,
        balance.value,
        balance.currency,
    ]);
    let chartSection;
    if (isInInvestWalkthrough) {
        chartSection = <Spacer h={16}/>;
    }
    else if (!hasInvestments) {
        chartSection = <AnimatedWave width={2700} height={250} strokeWidth={2.5}/>;
    }
    else {
        chartSection = (<>
          <Spacer h={16}/>
          <PortfolioChart loading={loading} isInTab={isInTab} contextValue={contextValue} refresh={refreshChart} changeMode={changeMode}/>
        </>);
    }
    return (<>
        <View style={[styles.portfolio, isInInvestWalkthrough && styles.walkthroughContainer]} onLayout={onPortfolioChartLayout}>
          <CardView noMargin style={isInInvestWalkthrough ? styles.walkthroughCard : null}>
            <View style={styles.titleContainer}>
              {isTradingAccountAlpaca ? (<Text Text-16 Secondary>
                  Portfolio
                </Text>) : (<View style={styles.row}>
                  <Text Text-16 Secondary>
                    Value
                  </Text>
                  <Spacer w={4}/>
                  <ToolTip small offset={5} colorKey="secondary" tootTipBtnText="Okay" title="Account value insights" 
        // A popover on web, so its text reads left-aligned there
        subtitle={<View>
                        <Text TextThin-14 Secondary centered={!isWeb}>
                          Value is the total amount in your account, including both investments and cash.
                        </Text>
                        <Text TextThin-14 Secondary centered={!isWeb} style={styles.middleRow}>
                          Total return shows your overall profit or loss, including investment performance, dividends,
                          interest, fees, taxes, and more.
                        </Text>
                        <Text TextThin-14 Secondary centered={!isWeb}>
                          Rate of return shows how much your account has grown or fallen as a percentage. It uses a
                          money-weighted method, which adjusts for the timing and size of any deposits or withdrawals,
                          meaning earlier or larger contributions have a bigger impact on your return than recent or
                          smaller ones.
                        </Text>
                      </View>}/>
                </View>)}
            </View>
            {balanceContainer}
            {chartSection}
          </CardView>
        </View>
        <ClaimFreeShare />
      </>);
});
export default memo(PortfolioHeader);
const paddingHorizontal = rem(16);
const styleSet = createStyleSheets((colors) => ({
    titleContainer: {
        paddingTop: rem(16),
        paddingHorizontal: rem(16),
    },
    balanceContainer: {
        paddingHorizontal,
    },
    balance: {
        fontSize: rem(36),
        lineHeight: Platform.OS === 'ios' ? rem(43) : rem(42),
        fontFamily: 'Nunito',
        fontWeight: '800',
        color: colors.text.primary,
        padding: 0,
        marginTop: rem(8),
    },
    moveUp: {
        marginTop: Platform.OS === 'android' ? -rem(8) : 0,
    },
    blurBalance: {
        marginHorizontal: rem(8),
    },
    portfolio: {
        paddingVertical: rem(8),
        paddingHorizontal: rem(16),
    },
    walkthroughContainer: {
        paddingBottom: 0,
        paddingTop: rem(2),
        paddingHorizontal: rem(4),
    },
    row: {
        flexDirection: 'row',
    },
    currencyStyles: {
        margin: 0,
        padding: 0,
        fontSize: rem(30),
        lineHeight: rem(isIOS ? 41 : 42),
        color: colors.text.primary,
        fontFamily: 'Nunito-ExtraBold',
    },
    absAmountStyles: {
        margin: 0,
        padding: 0,
        fontSize: rem(36),
        lineHeight: rem(48),
        color: colors.text.primary,
        fontFamily: 'Nunito-ExtraBold',
    },
    fractionStyles: {
        margin: 0,
        padding: 0,
        fontSize: rem(24),
        lineHeight: rem(38),
        color: colors.text.primary,
        paddingTop: rem(isIOS ? 0 : 4),
        fontFamily: 'Nunito-ExtraBold',
    },
    amountView: {
        paddingTop: rem(6),
        flexDirection: 'row',
    },
    middleRow: {
        marginVertical: rem(10),
    },
    walkthroughCard: {
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
    },
}));
